/**
 * PartnerShell (EPIC 40 S7) — minimal chrome for the /partner/* portal.
 * Same unified-app pattern as ClientShell, but with a single-page nav:
 * the home dashboard lists open offers + awarded jobs; per-job pages hang
 * off it.
 */

import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import { signOutCurrent } from '../services/firebase';

import { LanguageSwitcherMui } from './LanguageSwitcherMui';
import {
  shellActionsSpacing,
  shellContentSx,
  shellDrawerPaperSx,
  shellHeaderSx,
  shellMainSx,
  shellRootSx,
  shellTitleSx,
} from './shellStyles';


const DRAWER_WIDTH = 248;

const NAV_ITEMS = [{ to: '/partner', key: 'home', Icon: DashboardOutlinedIcon }] as const;

function getPageKey(pathname: string): string {
  if (pathname.startsWith('/partner/jobs/')) return 'job';
  return 'home';
}

export function PartnerShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation(['partner', 'common']);
  const { user } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const activeKey = getPageKey(location.pathname);
  const displayName = user?.displayName ?? user?.email ?? '';
  const initials = displayName
    .split(/[\s@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  const sidebar = (
    <Box
      role="navigation"
      sx={{ width: DRAWER_WIDTH, height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ px: 2.5, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          component="img"
          src="/rm24_logo.jpg"
          alt={t('common:app.name')}
          sx={{ width: 36, height: 36, borderRadius: 1.5, objectFit: 'cover' }}
        />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.1 }}>
            {t('common:app.name')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('partner:shell.portalLabel')}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ mx: 1 }} />
      <List sx={{ py: 1, flexGrow: 1 }}>
        {NAV_ITEMS.map(({ to, key, Icon }) => (
          <ListItemButton
            key={to}
            component={NavLink}
            to={to}
            selected={activeKey === key}
            onClick={() => setMobileOpen(false)}
          >
            <ListItemIcon>
              <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t(`partner:nav.${key}`)}
              primaryTypographyProps={{ fontSize: 14, fontWeight: activeKey === key ? 600 : 500 }}
            />
          </ListItemButton>
        ))}
      </List>
      <Divider sx={{ mx: 1 }} />
      <Box sx={{ p: 1 }}>
        <ListItemButton onClick={() => void signOutCurrent()}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t('common:actions.signOut')}
            primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={shellRootSx}>
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            // Same as the other shells: a closed keepMounted modal is only
            // `visibility: hidden`, so its box stretched the page wider than
            // the phone and everything scrolled sideways.
            '&.MuiModal-hidden': { display: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', ...shellDrawerPaperSx },
          }}
        >
          {sidebar}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', ...shellDrawerPaperSx },
          }}
        >
          {sidebar}
        </Drawer>
      </Box>

      <Box
        sx={shellContentSx(DRAWER_WIDTH)}
      >
        <Toolbar
          disableGutters
          sx={shellHeaderSx}
        >
          <IconButton
            edge="start"
            onClick={() => setMobileOpen((v) => !v)}
            sx={{ mr: 1.5, display: { md: 'none' } }}
            aria-label="menu"
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h2" noWrap sx={shellTitleSx}>
            {t(`partner:pageTitles.${activeKey}`)}
          </Typography>

          <Stack direction="row" spacing={shellActionsSpacing} alignItems="center">
            <LanguageSwitcherMui />
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ pl: 1 }}>
              <Avatar
                sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14, fontWeight: 600 }}
              >
                {initials || 'P'}
              </Avatar>
              <Box sx={{ display: { xs: 'none', sm: 'block' }, lineHeight: 1.2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('partner:shell.role')}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Toolbar>

        <Box component="main" sx={shellMainSx}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
